<template>
  <div class="query-builder-group" :id="vm.id" ref="selfGroup">
    <MenuBar class="query-builder-group-options" ref="queryBuilderGroupOptions" without-border>
      <template #leftItems>
        <div class="query-builder-group-options-logic">
          <div class="query-builder-group-options-logic-selection" @click="vm.localModel.logic = vm.localModel.logic == 'AND' ? 'OR' : 'AND'">
            <div class="and-selection" :class="{ active: vm.localModel.logic == 'AND' }">{{ $t("And") }}</div>
            <div class="or-selection" :class="{ active: vm.localModel.logic == 'OR' }">{{ $t("Or") }}</div>
          </div>
        </div>
      </template>
      <template #rightItems>
        <div class="query-builder-group-options-actions">
          <b-btn @click="emits('deleteGroup')" v-if="depth != 0" class="btn-icon mono delete mr-1" data-cy="group-clear">
            <Icon icon="Close" />
          </b-btn>
          <b-form-checkbox
            v-if="depth === 0 && 'isCaseSensitive' in vm.localModel"
            v-model="vm.localModel.isCaseSensitive"
            class="mr-3"
            data-cy="root-case-sensitive"
            >{{ $t("caseSensitive") }}</b-form-checkbox
          >
        </div>
      </template>
    </MenuBar>
    <div class="query-builder-rule-container">
      <template v-for="(rule, index) in rules" :key="`ruleId-${rule.ruleId}`">
        <div class="rule-wrapper" ref="ruleWrapper" :id="`rule-${getCurrentPath()}-${depth}-${index}`">
          <rule v-model="rules[index]" @deleteRule="onDeleteRule" :path="getCurrentPath()" @refresh-background="triggerRefresh" />
        </div>
      </template>
      <Alert v-if="!hasGroupRule" show with-icon variant="warning">{{ $t("AdvancedSearch.emptyGroup") }}</Alert>
      <div class="query-builder-rule-criteria-control" ref="queryBuilderRuleCriteriaControl">
        <MSelect
          data-cy="rule-conditions"
          class="query-builder-rule-criteria-selection"
          v-model="vm.selectedColumnForRule"
          :placeholder="$t('AddRulePlaceholder')"
          :items="possibleSelection"
          textField="localizedKey" />
        <b-btn class="muted" data-cy="advance-search-add-rule" @click="addRule()" :disabled="!vm.selectedColumnForRule">{{ $t("addCriteria") }}</b-btn>
        <span class="text-black-50 mx-2">{{ $t("or") }}</span>
        <template v-if="depth >= meta.config.advancedSearchMaxDepthAllowed - 1">
          <span v-tooltip="$t('api.errors.AdvancedSearch.MaxGroupLevelReached')" class="d-inline-block">
            <b-btn class="muted" data-cy="advance-search-add-group" @click="addGroup()" :disabled="depth >= meta.config.advancedSearchMaxDepthAllowed - 1">{{
              $t("addGroup")
            }}</b-btn>
          </span>
        </template>
        <b-btn v-else class="muted" data-cy="advance-search-add-group" @click="addGroup()">{{ $t("addGroup") }}</b-btn>
      </div>
    </div>
    <div class="query-builder-lower-group-container" v-if="groups.length">
      <template v-for="(group, index) in groups" :key="`group-${group.groupId}`">
        <div class="group-wrapper" ref="groupWrapper" :id="`group-${getCurrentPath()}-${depth}-${index}`">
          <group v-model="groups[index]" :depth="depth + 1" @deleteGroup="onDeleteGroup(group)" @refresh-background="triggerRefresh" :path="getCurrentPath()" />
        </div>
      </template>
    </div>
    <background />
  </div>
</template>

<script setup lang="ts">
import MSelect from "@/BasicComponentLibrary/MSelect.vue";
import Alert from "@common/components/Alert.vue";
import Icon from "@common/components/Icon.vue";
import MenuBar from "@common/components/MenuBar/MenuBar.vue";
import {
  FetchableData,
  FetchableDataEntry,
  QueryBuilderChildGroup,
  QueryBuilderData,
  QueryBuilderGroup,
  QueryBuilderRootGroup
} from "@medapro-types/AdvancedSearch/components/QueryBuilder";
import { RootState } from "@medapro-types/common/rootstate";
import { Guid } from "@util/guid";
import _ from "lodash";
import { Ref, computed, h, inject, nextTick, onMounted, reactive, ref, unref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useState } from "vuex-composition-helpers";
import Rule from "./Rule.vue";

//#region General
const { t: $t } = useI18n();
const { meta } = useState<RootState>(["meta"]);

const emits = defineEmits(["input", "deleteGroup", "refreshBackground"]);
const props = withDefaults(defineProps<{ value: QueryBuilderData; depth?: number; path: string }>(), { depth: 0 });

//#endregion

//#region refs
const selfGroup = ref<Element>();
const queryBuilderGroupOptions = ref<typeof MenuBar>();
const queryBuilderRuleCriteriaControl = ref<Element>();
const groupWrapper = ref<Element[]>([]);
const ruleWrapper = ref<Element[]>([]);
//#endregion

//#region Path

const getCurrentPath = () => {
  return `${props.path}|${vm.id}`;
};

//#endregion

//#region Fetched Data
const fetchedData = inject<Ref<FetchableData>>("fetchedData");

const hasGroupRule = computed<boolean>(() => {
  return vm.localModel.rules.length;
});

const possibleSelection = computed(() => {
  const unrefFetchedData = unref(fetchedData);
  if (!unrefFetchedData) return [];
  for (const item of unrefFetchedData) {
    item["localizedKey"] = $t("AdvancedSearch.SearchType." + item.searchType);
  }
  return _.sortBy(unrefFetchedData, (x) => x.localizedKey);
});
//#endregion

//#region TypeGuard
const isQueryBuilderGroup = (e: unknown): e is QueryBuilderGroup => {
  return !!e && Object.hasOwn(e, "groupId") && Object.hasOwn(e, "logic") && Object.hasOwn(e, "rules") && Object.hasOwn(e, "groups");
};
//#endregion

//#region ViewModel
type ViewModel = {
  id: string;
  localModel: QueryBuilderGroup;
  selectedColumnForRule: FetchableDataEntry | undefined;
  showAttributeGroupDialog: boolean;
  showAttributeDialog: boolean;
  isNewlyCreated: boolean;
};

const idHelper = Guid.new();

let initialModel: QueryBuilderGroup;

if (isQueryBuilderGroup(props.value)) {
  initialModel = props.value;
} else if (props.depth === 0) {
  initialModel = {
    groupId: idHelper,
    logic: "AND",
    isCaseSensitive: false,
    groups: [],
    rules: []
  } satisfies QueryBuilderRootGroup;
} else {
  initialModel = {
    groupId: idHelper,
    logic: "AND",
    groups: [],
    rules: []
  } satisfies QueryBuilderChildGroup;
}

const vm = reactive<ViewModel>({
  id: isQueryBuilderGroup(props.value) ? (props.value.groupId ?? idHelper) : idHelper,
  localModel: initialModel,
  selectedColumnForRule: undefined,
  showAttributeGroupDialog: false,
  showAttributeDialog: false,
  isNewlyCreated: true
});

//#endregion

//#region Rules
const rules = computed(() => {
  return vm.localModel.rules;
});

const addRule = () => {
  if (!vm.selectedColumnForRule) return;

  vm.localModel.rules.push({
    ruleId: Guid.new(),
    operator: null,
    searchType: vm.selectedColumnForRule.searchType,
    value: vm.selectedColumnForRule.uiType == "date" ? "unselected" : ""
  });
  vm.isNewlyCreated = false;
  triggerRefresh();
};

const onDeleteRule = (event: { id: string }) => {
  const index = vm.localModel.rules.findIndex((x) => x.ruleId == event.id);
  vm.localModel.rules.splice(index, 1);
  triggerRefresh();
};
//#endregion

//#region Groups
const groups = computed(() => {
  return vm.localModel.groups;
});

const addGroup = () => {
  vm.localModel.groups.push(getDefaultGroup());
  nextTick(() => {
    _.last(unref(groupWrapper))?.scrollIntoView();
  });
  triggerRefresh();
};
const getDefaultGroup = (): QueryBuilderChildGroup => {
  return { groupId: Guid.new(), logic: "AND", groups: [], rules: [] };
};

const onDeleteGroup = (group: QueryBuilderChildGroup) => {
  const index = vm.localModel.groups.indexOf(group);
  vm.localModel.groups.splice(index, 1);
  triggerRefresh();
};
//#endregion

//#region Update vModel
watch(
  () => props.value,
  () => {
    if (isQueryBuilderGroup(props.value)) {
      vm.localModel = props.value;
    } else if (props.depth === 0) {
      vm.localModel = {
        groupId: Guid.new(),
        logic: "AND",
        isCaseSensitive: false,
        groups: [],
        rules: []
      } satisfies QueryBuilderRootGroup;
    } else {
      vm.localModel = {
        groupId: Guid.new(),
        logic: "AND",
        groups: [],
        rules: []
      } satisfies QueryBuilderChildGroup;
    }
    vm.isNewlyCreated = !hasGroupRule.value;
    triggerRefresh(50);
  }
);
watch(
  () => vm.localModel,
  (nv) => {
    emits("input", nv);
  },
  { deep: true, immediate: true }
);
//#endregion

//#region Background

const lineCoordinates = ref<{ x0: number; y0: number; x1: number; y1: number }[]>([]);

const refreshLineCoordiantes = () => {
  lineCoordinates.value = [];
  const optionRect = unref(queryBuilderGroupOptions)?.$el.getBoundingClientRect();

  const selfGroupRect = unref(selfGroup)?.getBoundingClientRect();
  const queryBuilderRuleCriteriaControlRect = unref(queryBuilderRuleCriteriaControl)?.getBoundingClientRect();
  const offsetX = -(selfGroupRect?.x ?? 0),
    offsetY = -(selfGroupRect?.y ?? 0);

  const lastGroupRect = _.last(unref(groupWrapper))?.getBoundingClientRect();
  const lowestElementPos: number =
    _.max([
      (lastGroupRect?.y ?? 0) + offsetY + (optionRect?.height ?? 0) / 2,
      (queryBuilderRuleCriteriaControlRect?.y ?? 0) + offsetY + (queryBuilderRuleCriteriaControlRect?.height ?? 0) / 2
    ]) ?? 0;

  const x0 = (optionRect?.x ?? 0) + offsetX + 10;
  const y0 = (optionRect?.y ?? 0) + (optionRect?.height ?? 0) / 2 + offsetY;
  const x1 = x0;
  const y1 = lowestElementPos;

  lineCoordinates.value.push({ x0, y0, x1, y1 });
  const ruleRects = unref(ruleWrapper).map((x) => x.getBoundingClientRect());
  if (queryBuilderRuleCriteriaControlRect) ruleRects.push(queryBuilderRuleCriteriaControlRect);
  for (const ruleRect of ruleRects) {
    lineCoordinates.value.push({
      x0: x1,
      y0: ruleRect.y + offsetY + ruleRect.height / 2,
      x1: ruleRect.x + offsetX + 0,
      y1: ruleRect.y + offsetY + ruleRect.height / 2
    });
  }

  for (const group of unref(groupWrapper)) {
    const groupRect = group.getBoundingClientRect();
    lineCoordinates.value.push({
      x0: x1,
      y0: groupRect.y + offsetY + optionRect.height / 2,
      x1: groupRect.x + offsetX /* + 20 */,
      y1: groupRect.y + offsetY + optionRect.height / 2
    });
  }

  nextTick(() => {
    emits("refreshBackground");
  });
};

const background = () => {
  return h("svg", { class: "background-lines-svg", style: `z-index:${-100 + props.depth};` }, [
    lineCoordinates.value.map((coort) => {
      return h("line", {
        class: "background-lines-svg-line",
        x1: coort.x0,
        y1: coort.y0,
        x2: coort.x1,
        y2: coort.y1
      });
    })
  ]);
};

const triggerRefresh = (triggerAfterMS: number = 0) => {
  setTimeout(() => {
    nextTick(() => {
      refreshLineCoordiantes();
    });
  }, triggerAfterMS);
};
//#endregion

//#region LifeCycle
onMounted(() => {
  if (!vm.localModel.groupId) {
    vm.localModel.groupId = vm.id;
  }
  vm.isNewlyCreated = !hasGroupRule.value;
  triggerRefresh(50);
});
//#endregion
</script>
